import { Title, Text, Badge } from "rizzui";
import Table from "@/components/legacy-table";
import { Order } from "@/types";
import { formatDate } from "@/utils/format-date";
import { Link } from "react-router-dom";
import { stat } from "fs";
import { getStatusColors } from "@/components/table-utils/get-status-color";
import Filters from "../../shipments/list/filters";
import { useTanStackTable } from "@/components/table/custom/use-TanStack-Table";
import { orderListColumns } from "../list/columns";
import EyeIcon from "@/components/icons/eye";

export const getColumns = () => [
    // orderId
    {
        title: <span className="block">Order ID</span>,
        dataIndex: "orderId",
        key: "orderId",
        width: 300,
        render: (orderId: string) => <p className="text-primary">{orderId}</p>,
    },
    // client
    {
        title: <span className="block">Client</span>,
        dataIndex: "client",
        key: "client",
        width: 300,
        render: (client: { fullname: string }) => <p>{client.fullname}</p>,
    },
    // product name
    {
        title: <span className="block">Product</span>,
        dataIndex: "product",
        key: "product",
        width: 300,
        render: (product: { name: string }) => <p>{product.name}</p>,
    },
    // status
    {
        title: <span className="block">Status</span>,
        dataIndex: "status",
        key: "status",
        width: 300,
        render: (status: string) => (
            <div className="flex items-center gap-1.5">
                <Badge variant="flat" color={getStatusColors(status)}>
                    {status}
                </Badge>
            </div>
        ),
    },
    // category
    {
        title: <span className="block">Category</span>,
        dataIndex: "product",
        key: "category",
        width: 300,
        render: (product: { category: { name: string } }) => (
            <p>{product.category.name}</p>
        ),
    },
    // quantity
    {
        title: <span className="block">Quantity</span>,
        dataIndex: "quantity",
        key: "quantity",
        width: 300,
        render: (quantity: string) => <p>{quantity}</p>,
    },
    {
        title: <span className="ms-6 block whitespace-nowrap">Date</span>,
        dataIndex: "createdDate",
        key: "createdDate",
        width: 200,
        render: (createdDate: string) => (
            <span className="ms-6 block">
                <Text className="mb-1 font-medium text-gray-700">
                    {formatDate(new Date(createdDate), "MMM D, YYYY")}
                </Text>
                {/* <Text className="text-[13px] text-gray-500">
                    {formatDate(new Date(createdDate), "h:mm A")}
                </Text> */}
            </span>
        ),
    },
    {
        title: <span className="block">Actions</span>,
        dataIndex: "id",
        key: "id",
        width: 200,
        render: (id: string) => (
            <div className="flex items-center justify-center">
                <Link
                    to={`/orders/${id}`}
                    className="text-primary hover:text-primary-dark"
                >
                    <EyeIcon className="h-6 w-6" />
                </Link>
            </div>
        ),
    },
];

function OrderReportTable({ orders }: { orders: Order[] }) {
    const { table, setData } = useTanStackTable<Order>({
        tableData: orders,
        columnConfig: orderListColumns,
        options: {
            initialState: {
                pagination: {
                    pageIndex: 0,
                    pageSize: 10,
                },
            },
            meta: {
                handleDeleteRow: (row) => {
                    setData((prev) => prev.filter((r) => r.id !== row.id));
                    table.resetRowSelection();
                },
                handleMultipleDelete: (rows) => {
                    setData((prev) => prev.filter((r) => !rows.includes(r)));
                    table.resetRowSelection();
                },
            },
            enableColumnResizing: false,
        },
    });

    const handleFilterChange = (filters: any) => {
        let filteredData = orders;

        if (filters.globalFilter) {
            filteredData = filteredData.filter((order) =>
                Object.values(order).some((value) =>
                    String(value)
                        .toLowerCase()
                        .includes(filters.globalFilter.toLowerCase()),
                ),
            );
        }

        if (filters.date) {
            filteredData = filteredData.filter(
                (order) =>
                    new Date(order.createdDate) >= new Date(filters.date[0]) &&
                    new Date(order.createdDate) <= new Date(filters.date[1]),
            );
        }

        if (filters.status) {
            filteredData = filteredData.filter((order) =>
                filters.status.includes(order.status),
            );
        }

        if (filters.paymentMethod) {
            filteredData = filteredData.filter((order) =>
                filters.paymentMethod.includes(order.paymentMethod),
            );
        }

        table.setGlobalFilter(filters.globalFilter || "");
        setData(filteredData);
    };

    return (
        <>
            <div className="flex items-center justify-between mb-6">
                <Filters table={table} onFilterChange={handleFilterChange} />
            </div>
            <Table
                data={
                    table.getRowModel().rows.length
                        ? table.getRowModel().rows.map((row) => row.original)
                        : orders
                }
                columns={getColumns()}
                variant="elegant"
                rowKey={(record: any) => record.id}
                scroll={{ x: 660 }}
                className="mb-11"
            />
        </>
    );
}

type OrderDetailsProps = {
    Orders: Order[];
};

export default function OrderReport({ Orders }: OrderDetailsProps) {
    var totalAmountCredit = Orders.reduce(
        (acc, curr) => acc + curr.totalPrice,
        0,
    );

    var totalDebitAmount = Orders.reduce(
        (acc, curr) => acc + curr.totalPrice,
        0,
    );
    return (
        <div className="w-full rounded-xl border border-muted p-5 text-sm sm:p-6 lg:p-8 2xl:p-10">
            {/* <div className="mb-12 flex flex-col-reverse items-start justify-between md:mb-16 md:flex-row">
                <img
                    src={siteConfig.logo ?? ""}
                    alt={siteConfig.title}
                    className="dark:invert"
                    width={80}
                    height={80}
                />
                <div className="mb-4 md:mb-0">
                    <Badge
                        variant="flat"
                        color="success"
                        rounded="md"
                        className="mb-3 md:mb-2"
                    >
                        {order?.status}
                    </Badge>
                    <Title as="h6">INV - #{order?.orderId}</Title>
                    <Text className="mt-0.5 text-gray-500">Order Number</Text>
                </div>
            </div>

            <div className="mb-12 grid gap-4 xs:grid-cols-2 sm:grid-cols-3 sm:grid-rows-1">
                <div className="">
                    <Title as="h6" className="mb-3.5 font-semibold">
                        From
                    </Title>
                    <Text className="mb-1.5 text-sm font-semibold uppercase">
                        Afro Cargo
                    </Text>
                    <Text className="mb-1.5">Jerome Bell</Text>
                    <Text className="mb-1.5">
                        4140 Rard Rd. Saka, <br /> China 31134
                    </Text>
                    <Text className="mb-4 sm:mb-6 md:mb-8">(+08) 555-0107</Text>
                    <div>
                        <Text className="mb-2 text-sm font-semibold">
                            Printed Date
                        </Text>
                        <Text>{new Date().toDateString()}</Text>
                    </div>
                </div>

                <div className="mt-4 xs:mt-0">
                    <Title as="h6" className="mb-3.5 font-semibold">
                        Orderd To
                    </Title>
                    <Text className="mb-1.5 text-sm font-semibold uppercase">
                        {order?.client?.fullname}
                    </Text>
                    <Text className="mb-1.5">{order?.client?.email}</Text>
                    <Text className="mb-1.5">{order?.shippingAddress}</Text>
                    <Text className="mb-4 sm:mb-6 md:mb-8">
                        +{order?.client?.phoneNumber}
                    </Text>
                    <div>
                        <Text className="mb-2 text-sm font-semibold">
                            Due Date
                        </Text>

                        <Text>
                            {new Date(
                                new Date().setDate(new Date().getDate() + 30),
                            ).toDateString()}
                        </Text>
                    </div>
                </div>

                <div className="mt-4 flex sm:mt-6 md:mt-0 md:justify-end">
                    <QRCodeSVG
                        value={"http://localhost:5172/orders/" + order?.id}
                        className="h-28 w-28 lg:h-32 lg:w-32"
                    />
                </div>
            </div> */}

            <OrderReportTable orders={Orders} />

            <div className="flex flex-col-reverse items-start justify-between border-t border-muted pb-4 pt-8 xs:flex-row">
                {/* <div className="mt-6 max-w-md pe-4 xs:mt-0">
                    <Title
                        as="h6"
                        className="mb-1 text-xs font-semibold uppercase xs:mb-2 xs:text-sm"
                    >
                        Notes
                    </Title>
                    <Text className="leading-[1.7]">
                        Please make sure to pay the Order before the due date.
                        If you have any questions, please contact us at{" "}
                        <a
                            href="mailto:support@afroCargo.com"
                            className="text-primary"
                        >
                            support@afroCargo.com
                        </a>
                    </Text>
                </div> */}
                {/* <div className="w-full max-w-sm">
                    <Text className="flex items-center justify-between border-b border-muted py-3.5 lg:py-5">
                        Payments:{" "}
                        <Text as="span" className="font-semibold">
                            ${totalAmountCredit}
                        </Text>
                    </Text>
                    <Text className="flex items-center justify-between border-b border-muted py-3.5 lg:py-5">
                        Invoices:{" "}
                        <Text as="span" className="font-semibold">
                            ${totalDebitAmount}
                        </Text>
                    </Text>
                    <Text className="flex items-center justify-between border-b border-muted py-3.5 lg:py-5">
                        Taxes:
                        <Text as="span" className="font-semibold">
                            0
                        </Text>
                    </Text>
                    <Text className="flex items-center justify-between pt-4 text-base font-semibold text-gray-900 lg:pt-5">
                        Total Amount to be Paid:{" "}
                        <Text as="span">
                            {totalAmountCredit < totalDebitAmount ? (
                                <span className="text-md font-bold text-red-500">
                                    ${totalDebitAmount - totalAmountCredit}
                                </span>
                            ) : (
                                <span className="text-md font-bold text-green-500">
                                    ${totalAmountCredit - totalDebitAmount}
                                </span>
                            )}
                        </Text>
                    </Text>
                </div> */}
            </div>
        </div>
    );
}
